import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wand2, Loader2, Upload, Link2, X } from 'lucide-react';
import { imageStudioService } from '../../services/imageStudioService';
import { GeneratedImage, ImageStyle, IMAGE_STYLES, IMAGE_SIZES } from '../../types/imageStudio';
import toast from 'react-hot-toast';
import AiCostBadge from '../ai/AiCostBadge';

interface ImageGeneratorProps {
  onImageGenerated: (image: GeneratedImage) => void;
  campaignId?: string;
  /** Pre-load a reference image URL (e.g. from gallery "use as reference" action) */
  initialReferenceUrl?: string | null;
  /** Called once the initial reference has been consumed so the parent can clear it */
  onReferenceConsumed?: () => void;
}

type ReferenceMode = 'upload' | 'url';

const ImageGenerator: React.FC<ImageGeneratorProps> = ({ onImageGenerated, campaignId, initialReferenceUrl, onReferenceConsumed }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState<ImageStyle>('realistic');
  const [size, setSize] = useState('1024x1024');
  const [title, setTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Reference image state (optional)
  const [referenceEnabled, setReferenceEnabled] = useState(false);
  const [referenceMode, setReferenceMode] = useState<ReferenceMode>('upload');
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [referencePreview, setReferencePreview] = useState<string | null>(null);
  const [referenceUrl, setReferenceUrl] = useState('');
  const [isUploadingReference, setIsUploadingReference] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Pre-fill reference URL when parent requests it (e.g. "use as reference" from gallery)
  React.useEffect(() => {
    if (initialReferenceUrl) {
      setReferenceEnabled(true);
      setReferenceMode('url');
      setReferenceUrl(initialReferenceUrl);
      setReferenceFile(null);
      setReferencePreview(initialReferenceUrl);
      onReferenceConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReferenceUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('imageStudio.reference.onlyImages', 'Only image files are allowed.'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('imageStudio.reference.tooBig', 'Reference image must be under 10 MB.'));
      return;
    }
    setReferenceFile(file);
    setReferencePreview(URL.createObjectURL(file));
  };

  const clearReference = () => {
    if (referencePreview) URL.revokeObjectURL(referencePreview);
    setReferenceFile(null);
    setReferencePreview(null);
    setReferenceUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * Resolve the reference image to a usable URL before generation.
   * - file: upload to S3 via the dedicated reference endpoint (no gallery record)
   * - url: return as-is (backend downloads and persists to S3)
   */
  const resolveReferenceUrl = async (): Promise<string | null> => {
    if (!referenceEnabled) return null;
    if (referenceMode === 'upload') {
      if (!referenceFile) return null;
      setIsUploadingReference(true);
      try {
        const uploaded = await imageStudioService.uploadReference(referenceFile);
        return uploaded?.url || null;
      } finally {
        setIsUploadingReference(false);
      }
    }
    const trimmed = referenceUrl.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(t('imageStudio.promptRequired', 'Please enter a prompt'));
      return;
    }

    setIsGenerating(true);
    try {
      let refUrl: string | null = null;
      try {
        refUrl = await resolveReferenceUrl();
      } catch (upErr: any) {
        toast.error(upErr?.response?.data?.message || upErr?.message || t('imageStudio.reference.uploadError', 'Failed to upload reference image.'));
        return;
      }

      const response = await imageStudioService.generateImage({
        prompt: prompt.trim(),
        style,
        size,
        title: title.trim() || undefined,
        campaign_id: campaignId,
        reference_image_url: refUrl || undefined
      });

      if (response.success && response.data) {
        toast.success(t('imageStudio.generated', 'Image generated successfully!'));
        onImageGenerated(response.data);
        setPrompt('');
        setTitle('');
        clearReference();
        setReferenceEnabled(false);
      }
    } catch (error: any) {
      const code = error.response?.data?.code || '';
      // PLAN_LIMIT_EXCEEDED: o interceptor global do api.ts já mostra o toast com CTA de upgrade
      if (code !== 'PLAN_LIMIT_EXCEEDED') {
        toast.error(error.response?.data?.message || error.message || t('imageStudio.generateError', 'Failed to generate image'));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Prompt */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('imageStudio.prompt', 'Describe your image')}
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t('imageStudio.promptPlaceholder', 'E.g.: A modern office space with natural lighting and green plants...')}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          rows={4}
          disabled={isGenerating}
        />
      </div>

      {/* Title (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('imageStudio.title_field', 'Title')} <span className="text-gray-400">({t('common.optional', 'optional')})</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('imageStudio.titlePlaceholder', 'Give your image a name')}
          className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          disabled={isGenerating}
        />
      </div>

      {/* Reference image (optional) */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50/60 dark:bg-gray-800/30">
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={referenceEnabled}
            onChange={(e) => {
              setReferenceEnabled(e.target.checked);
              if (!e.target.checked) clearReference();
            }}
            disabled={isGenerating}
            className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {t('imageStudio.reference.enable', 'Usar imagem de referência')}
              <span className="text-xs font-normal text-gray-400 ml-1">
                ({t('common.optional', 'optional')})
              </span>
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('imageStudio.reference.hint', 'A IA vai se basear nela para gerar a nova imagem.')}
            </p>
          </div>
        </label>

        {referenceEnabled && (
          <div className="mt-3 space-y-3">
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1">
              <button
                type="button"
                onClick={() => setReferenceMode('upload')}
                disabled={isGenerating}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  referenceMode === 'upload'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Upload className="w-3.5 h-3.5" />
                {t('imageStudio.reference.upload', 'Upload')}
              </button>
              <button
                type="button"
                onClick={() => setReferenceMode('url')}
                disabled={isGenerating}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  referenceMode === 'url'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Link2 className="w-3.5 h-3.5" />
                {t('imageStudio.reference.pasteUrl', 'Colar link')}
              </button>
            </div>

            {referenceMode === 'upload' ? (
              <div>
                {!referencePreview ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isGenerating}
                    className="w-full flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition text-sm text-gray-500 dark:text-gray-400"
                  >
                    <Upload className="w-5 h-5" />
                    {t('imageStudio.reference.clickToUpload', 'Clique para escolher uma imagem (até 10 MB)')}
                  </button>
                ) : (
                  <div className="relative inline-block">
                    <img
                      src={referencePreview}
                      alt="reference"
                      className="max-h-40 rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                    <button
                      type="button"
                      onClick={clearReference}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700"
                      title={t('common.remove', 'Remove') as string}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isGenerating}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="url"
                  value={referenceUrl}
                  onChange={(e) => { setReferenceUrl(e.target.value); setReferencePreview(e.target.value.trim() || null); }}
                  placeholder={t('imageStudio.reference.urlPlaceholder', 'https://…/imagem.jpg') as string}
                  disabled={isGenerating}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {referenceUrl.trim() && (
                  <div className="relative inline-block">
                    <img
                      src={referenceUrl}
                      alt="Reference"
                      className="max-h-32 rounded-lg border border-gray-200 dark:border-gray-700 object-contain"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={clearReference}
                      className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-red-500 text-white hover:bg-red-600 shadow"
                      title={t('imageStudio.reference.remove', 'Remove reference')}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Style Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('imageStudio.style', 'Style')}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {IMAGE_STYLES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStyle(s.value)}
              disabled={isGenerating}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                style === s.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <p className={`text-sm font-medium ${
                style === s.value ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-white'
              }`}>
                {t(`imageStudio.styles.${s.value}`, s.label)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t(`imageStudio.styles.${s.value}Desc`, s.description)}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Size Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('imageStudio.size', 'Size')}
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {IMAGE_SIZES.map((s) => (
            <button
              key={s.value}
              onClick={() => setSize(s.value)}
              disabled={isGenerating}
              className={`p-3 rounded-xl border-2 text-center transition-all ${
                size === s.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <p className={`text-sm font-medium ${
                size === s.value ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-white'
              }`}>
                {t(`imageStudio.sizes.${s.value}`, s.label)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.value}</p>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {t('imageStudio.sizesProviderNote', 'O provedor de IA suporta apenas estes 3 tamanhos. Após gerar, você pode redimensionar/cortar no editor.')}
        </p>
      </div>

      {/* Custo de IA da geração, saldo e recarga sem perder o prompt */}
      <AiCostBadge feature="ai-generation" action="image-studio-generate" unitLabel={t('aiCredits.perImage', 'por imagem')} />

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isGenerating || isUploadingReference || !prompt.trim()}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-purple-600 text-white font-medium hover:from-primary-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-primary-600/25"
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {isUploadingReference
              ? t('imageStudio.reference.uploading', 'Subindo imagem de referência...')
              : t('imageStudio.generating', 'Generating...')}
          </>
        ) : (
          <>
            <Wand2 className="w-5 h-5" />
            {t('imageStudio.generate', 'Generate Image')}
          </>
        )}
      </button>

      {/* Preview area during generation */}
      {isGenerating && (
        <div className="flex items-center justify-center p-12 rounded-xl bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-300 dark:border-gray-600">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary-500 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('imageStudio.generatingMessage', 'Creating your image with AI...')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGenerator;
